import i18next from 'i18next'

interface MedicationSchedule {
  frequency?: string
  frequencyDayOfWeek?: number
  frequencyOrdinal?: string
  frequencyDayOfMonth?: number
  frequencyAnchorDate?: number
  oneTimeDate?: string
  remindDayBefore?: boolean
}

const DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

/**
 * Returns a human-readable schedule description for a medication.
 * Uses i18n keys from medicationManagement.schedule.
 */
export function formatScheduleText(medication: MedicationSchedule): string {
  const t = i18next.t.bind(i18next)
  const frequency = medication.frequency ?? 'daily'

  switch (frequency) {
    case 'daily':
      return t('medicationManagement.schedule.everyDay')

    case 'weekly': {
      const dayKey = DAY_KEYS[medication.frequencyDayOfWeek ?? 0]
      const day = t(`medicationManagement.schedule.days.${dayKey}`)
      return t('medicationManagement.schedule.everyWeek', { day })
    }

    case 'biweekly': {
      const dayKey = DAY_KEYS[medication.frequencyDayOfWeek ?? 0]
      const day = t(`medicationManagement.schedule.days.${dayKey}`)
      return t('medicationManagement.schedule.everyOtherWeek', { day })
    }

    case 'monthly_day_of_week': {
      const dayKey = DAY_KEYS[medication.frequencyDayOfWeek ?? 0]
      const dayName = t(`medicationManagement.schedule.days.${dayKey}`)
      const ordinalText = t(
        `medicationManagement.schedule.ordinals.${medication.frequencyOrdinal ?? '1st'}`
      )
      // Use manual interpolation to avoid i18next reserved 'ordinal' key conflict
      const template = t('medicationManagement.schedule.monthlyOrdinalDay') as string
      return template.replace('{{ordinal}}', ordinalText).replace('{{day}}', dayName)
    }

    case 'monthly_date': {
      const date = formatOrdinalNumber(medication.frequencyDayOfMonth ?? 1)
      return t('medicationManagement.schedule.monthlyOnDate', { date })
    }

    case 'one_time': {
      if (!medication.oneTimeDate) return t('medicationManagement.schedule.oneTime')
      const [year, month, day] = medication.oneTimeDate.split('-').map(Number)
      const dateObj = new Date(year, month - 1, day)
      const formattedDate = dateObj.toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
      const text = t('medicationManagement.schedule.oneTimeOnDate', { date: formattedDate })
      if (medication.remindDayBefore) {
        return text + ' ' + t('medicationManagement.schedule.withDayBeforeReminder')
      }
      return text
    }

    default:
      return t('medicationManagement.schedule.everyDay')
  }
}

/**
 * Determines whether a medication should be scheduled on a given date.
 */
export function shouldScheduleOnDate(medication: MedicationSchedule, date: Date): boolean {
  const frequency = medication.frequency ?? 'daily'

  switch (frequency) {
    case 'daily':
      return true

    case 'weekly':
      return date.getDay() === (medication.frequencyDayOfWeek ?? 0)

    case 'biweekly': {
      if (date.getDay() !== (medication.frequencyDayOfWeek ?? 0)) {
        return false
      }
      const anchorDate = medication.frequencyAnchorDate
        ? new Date(medication.frequencyAnchorDate)
        : date
      const anchorWeek = getISOWeekNumber(anchorDate)
      const currentWeek = getISOWeekNumber(date)
      // Adjust for year boundary
      const anchorYear = getISOWeekYear(anchorDate)
      const currentYear = getISOWeekYear(date)
      const totalAnchorWeeks = anchorYear * 53 + anchorWeek
      const totalCurrentWeeks = currentYear * 53 + currentWeek
      return (totalCurrentWeeks - totalAnchorWeeks) % 2 === 0
    }

    case 'monthly_day_of_week': {
      const targetDay = medication.frequencyDayOfWeek ?? 0
      if (date.getDay() !== targetDay) {
        return false
      }
      const ordinal = medication.frequencyOrdinal ?? '1st'
      return isNthOccurrenceOfDay(date, ordinal)
    }

    case 'monthly_date': {
      const targetDate = medication.frequencyDayOfMonth ?? 1
      const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
      // If target is beyond last day of month (e.g., 31st in a 30-day month), use last day
      if (targetDate > lastDayOfMonth) {
        return date.getDate() === lastDayOfMonth
      }
      return date.getDate() === targetDate
    }

    case 'one_time': {
      if (!medication.oneTimeDate) return false
      const [year, month, day] = medication.oneTimeDate.split('-').map(Number)
      const targetDate = new Date(year, month - 1, day)
      const isSameDay =
        date.getFullYear() === targetDate.getFullYear() &&
        date.getMonth() === targetDate.getMonth() &&
        date.getDate() === targetDate.getDate()
      if (isSameDay) return true
      if (medication.remindDayBefore) {
        const dayBefore = new Date(targetDate)
        dayBefore.setDate(dayBefore.getDate() - 1)
        return (
          date.getFullYear() === dayBefore.getFullYear() &&
          date.getMonth() === dayBefore.getMonth() &&
          date.getDate() === dayBefore.getDate()
        )
      }
      return false
    }

    default:
      return true
  }
}

/**
 * Returns the ISO 8601 week number for a given date.
 */
export function getISOWeekNumber(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  // Set to nearest Thursday: current date + 4 - current day number
  // Make Sunday = 7
  const dayNum = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
}

/**
 * Returns the ISO week-numbering year for a date.
 */
function getISOWeekYear(date: Date): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  const dayNum = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum)
  return d.getUTCFullYear()
}

/**
 * Checks if a date is the Nth occurrence of its weekday in its month.
 */
function isNthOccurrenceOfDay(date: Date, ordinal: string): boolean {
  const dayOfMonth = date.getDate()
  const dayOfWeek = date.getDay()

  if (ordinal === 'last') {
    // Check if there's no more occurrence of this day in the month
    const nextOccurrence = dayOfMonth + 7
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
    return nextOccurrence > lastDayOfMonth
  }

  const ordinalMap: Record<string, number> = {
    '1st': 1,
    '2nd': 2,
    '3rd': 3,
    '4th': 4,
  }

  const targetN = ordinalMap[ordinal]
  if (!targetN) return false

  // Count how many times this weekday has occurred up to and including this date
  // First occurrence is on day 1-7, second on 8-14, etc.
  let count = 0
  for (let d = 1; d <= dayOfMonth; d++) {
    const checkDate = new Date(date.getFullYear(), date.getMonth(), d)
    if (checkDate.getDay() === dayOfWeek) {
      count++
    }
  }

  return count === targetN
}

/**
 * Formats a number as an ordinal (1st, 2nd, 3rd, etc.)
 */
function formatOrdinalNumber(n: number): string {
  const suffixes: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' }
  const v = n % 100
  return n + (suffixes[(v - 20) % 10] || suffixes[v] || 'th')
}
